/**
 * Unblur Picture Gallery Item Thumbnail Images
 *
 * @module
 *
 * @see [ACNG-9026](https://ivg.atlassian.net/browse/ACNG-9026)
 */

/**
 * @import {Picture} from "../model/picture"
 * @import {PictureSet} from "../model/picture-set";
 * @import {FriggImageData} from "../../core/model/image.js";
 */

import {guard, RECORD, REGEX} from '@acng/frontend-rubicon';
import {get} from '../../core/service/backend.js';
import {IMAGE_DATA} from '../../core/model/image.js';
import {GlobalWatch} from '@acng/frontend-relativity';
import {ctxStockUpdate} from '../../userPool/context/stock-update.js';

/**
 * @typedef {{[pictureId: Picture['id']]: {url: string, frigg: FriggImageData} | undefined}} ThumbnailList
 */

/**
 * @param {PictureSet['id']} setId
 *
 * @returns {Promise<ThumbnailList>}
 */
const fetchImageList = async (setId) => {
  const thumbnailList = await get(`picture/thumbnails/${setId}`);
  ASSERT: guard(thumbnailList, RECORD(REGEX(/^\d+$/), IMAGE_DATA));

  return thumbnailList;
};

/**
 * Cache could be optimized like WeakMap<PictureSet, ...>
 *
 * @type {Map<Picture['set_id'], Promise<ThumbnailList>>}
 */
const cache = new Map();

GlobalWatch(ctxStockUpdate)(async (stockItem) => {
  if (!stockItem || stockItem.type !== 'Picture') return;

  for (const [setId, listPromise] of cache.entries()) {
    let list;
    try {
      list = await listPromise;
    } catch {
      cache.delete(setId);
      continue;
    }

    if (!list[stockItem.article_id]) continue;

    list[stockItem.article_id] = stockItem.thumbnail;
    cache.set(setId, Promise.resolve(list));
    return;
  }
});

/**
 * @param {FriggImageData} thumbnail
 *
 * @returns {string}
 */
const generateThumbnailUrl = (thumbnail) => {
  const {base, hash} = thumbnail;

  return hash ? `${base}-pw.jpg?u=${hash}` : `${base}-bpw.jpg`;
}

/**
 * @param {Picture} picture
 *
 * @returns {Promise<string>}
 */
const unblurThumbnail = async (picture) => {
  const {id, set_id, thumbnail: fallback} = picture;
  const cached = cache.get(set_id);

  if (cached) {
    const thumbnailList = await cached;
    const thumbnail = thumbnailList[id]?.frigg;

    if (!thumbnail) {
      ASSERT: console.warn('Picture id not in decoded response data.', {picture, thumbnailList});
      cache.delete(set_id);

      return fallback;
    }

    return generateThumbnailUrl(thumbnail);

  } else {
    const futureList = fetchImageList(set_id);
    cache.set(set_id, futureList);

    try {
      const list = await futureList;
      const thumbnail = list[id]?.frigg;

      return thumbnail ? generateThumbnailUrl(thumbnail) : fallback;
    } catch (e) {
      cache.delete(set_id);
      console.error(e);
      DEBUG: console.info('Use blurred thumbnail image after error.');

      return fallback;
    }
  }
};

export {unblurThumbnail};
